import React, { FC, useState, useMemo, useCallback } from 'react';
import styled from '@emotion/styled';
import { Box, OutlinedButton } from '~Styles';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { useButton, useLogger, useI18n } from '~Hooks';
import { useEntryFinder } from '~Components/EntryFinder';
import { CompleteBongQuery_venue_company_categories_items as Item } from '~Scenes/POS/Queries/__queries__';
import ItemButton from '../ItemButton';
import { ItemCategoryMap } from '~Scenes/POS/categories';
import { LocalVariation, LocalItem } from '~Types';
import Icon from '~ComponentsImproved/Icon';
import NextIcon from '~Assets/icons/arrow_forward.svg';
import PrevIcon from '~Assets/icons/arrow_back.svg';
import FinishIcon from '~Assets/icons/check.svg';

const ItemsGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-auto-rows: 88px;
  gap: 8px;
  overflow-y: auto;
  overflow-x: hidden;
  align-content: flex-start;
  padding: 0 4px 120px;
  box-sizing: border-box;

  &::-webkit-scrollbar {
    display: none;
  }

  & > * {
    margin: 0 !important;
    padding: 0;
    width: 100% !important;
    max-width: 100% !important;
  }
`;

const StageHeader = styled(Box)`
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: var(--colorBlueGray1300);
  padding: 8px 16px;
  font-weight: 600;
  margin-bottom: 8px;
  min-height: 64px;
`;

const OfferTitle = styled.p`
  color: var(--colorUIOrange);
  font-size: 14px;
  margin: 0;
`;

interface StageItem {
  id: string;
  title: string;
}

interface Stage {
  id: string;
  title: {
    se?: string;
    en?: string;
    es?: string;
    no?: string;
  };
  items: StageItem[];
}

interface CategoryItem {
  id: string;
  itemId: string;
  title: string;
  color: string | undefined;
  descriptionLang: any;
  manualPrice: boolean;
  volumeSuffix: string | null | undefined;
}

interface Props {
  offerName: string;
  stages: Stage[];
  categoryItems: CategoryItem[];
  allItems: Item[];
  venueId: string;
  itemToCategoryMap: ItemCategoryMap;
  count: number;
  items: LocalItem[];
  nrInCartMap: { [itemId: string]: number };
  onAddItem: (
    id: string,
    title: string,
    categoryId: string,
    options: {
      variations: LocalVariation[];
      removedVariations: LocalVariation[];
      message?: string | null;
      type?: string;
      count?: number;
      courseType?: any;
      isMessage?: boolean;
      parentId?: string;
    },
    price?: number,
  ) => { cartItemIds: string[] };
  onComplete: () => void;
  hasServiceCharge?: boolean;
  isScrolling: boolean;
}

const StagedBundleGrid: FC<Props> = ({
  offerName,
  stages,
  categoryItems,
  allItems,
  venueId,
  itemToCategoryMap,
  count,
  items,
  nrInCartMap,
  onAddItem,
  onComplete,
  hasServiceCharge,
  isScrolling,
}) => {
  const logger = useLogger('staged-bundle-grid');
  const { i18n } = useI18n();
  const findEntryFromItem = useEntryFinder();

  const [currentStageIndex, setCurrentStageIndex] = useState(0);

  const currentStage = stages[currentStageIndex];
  const isLastStage = currentStageIndex === stages.length - 1;

  // Sort items by category order (descending), then alphabetically by title within each category
  const sortedItems = useMemo(() => {
    if (!currentStage?.items) return [];

    return [...currentStage.items]
      .filter((item) => {
        // Filter out items that don't exist in the menu
        const entry = findEntryFromItem(item.id);
        if (!entry) {
          logger.warn(`Item ${item.id} not found in menu, filtering out from stage`);
          return false;
        }
        return true;
      })
      .map((item) => {
        const foundItem = allItems.find((i) => i.id === item.id);
        const categoryItem = categoryItems.find((ci) => ci.itemId === item.id);
        const category = itemToCategoryMap[item.id];

        return {
          itemId: item.id,
          title: foundItem ? i18n(foundItem.titleLang) || item.title : item.title,
          color: categoryItem?.color || '#fff',
          descriptionLang: categoryItem?.descriptionLang,
          manualPrice: categoryItem?.manualPrice || false,
          volumeSuffix: categoryItem?.volumeSuffix || null,
          categoryId: categoryItem?.id || item.id,
          categoryOrder: category?.order ?? 0, // Use 0 as default if category not found
        };
      })
      .sort((a, b) => {
        // First sort by category order (descending - higher order first)
        const orderDiff = b.categoryOrder - a.categoryOrder;
        if (orderDiff !== 0) return orderDiff;

        // Then sort alphabetically by title within the same category
        return a.title.localeCompare(b.title);
      });
  }, [currentStage, findEntryFromItem, allItems, categoryItems, i18n, itemToCategoryMap]);

  // Get translated stage title
  const stageTitle = useMemo(() => {
    if (!currentStage?.title) return `Stage ${currentStageIndex + 1}`;
    return i18n(currentStage.title) || `Stage ${currentStageIndex + 1}`;
  }, [currentStage, currentStageIndex, i18n]);

  // Add items to cart without auto-advancing
  const handleAddItem = useCallback(
    (
      id: string,
      title: string,
      categoryId: string,
      options: {
        variations: LocalVariation[];
        removedVariations: LocalVariation[];
        message?: string | null;
        type?: string;
        count?: number;
        courseType?: any;
        isMessage?: boolean;
        parentId?: string;
      },
      price?: number,
    ) => {
      logger.info('Item added in staged bundle', {
        itemId: id,
        stageIndex: currentStageIndex,
      });

      // Add item to cart (no auto-advance)
      return onAddItem(id, title, categoryId, options, price);
    },
    [onAddItem, currentStageIndex, logger],
  );

  const nextButton = useButton({
    onClick: () => {
      if (isLastStage) {
        onComplete();
      } else {
        setCurrentStageIndex(currentStageIndex + 1);
      }
    },
  });

  const prevButton = useButton({
    onClick: () => {
      if (currentStageIndex > 0) {
        setCurrentStageIndex(currentStageIndex - 1);
      } else {
        onComplete(); // Same as "Canceling"
      }
    },
  });

  return (
    <>
      {/* Stage Header */}
      <StageHeader>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <div>
            <OfferTitle>{offerName}</OfferTitle>
            <Heading level={4} color="var(--colorWhite)">
              {stageTitle}
            </Heading>
          </div>
          <Box display="flex" alignItems="center" gap="small">
            <OutlinedButton {...prevButton} size="small" padding="xs">
              <Icon src={PrevIcon} color="black" />
            </OutlinedButton>
            <Paragraph level={4} color="var(--colorBlueGray200)" style={{ whiteSpace: 'nowrap' }}>
              {i18n('POS.Stage')} {currentStageIndex + 1} / {stages.length}
            </Paragraph>
            <OutlinedButton {...nextButton} size="small" padding="xs">
              {isLastStage ? (
                <Icon src={FinishIcon} color="black" />
              ) : (
                <Icon src={NextIcon} color="black" />
              )}
            </OutlinedButton>
          </Box>
        </Box>
      </StageHeader>

      {/* Items Grid */}
      <ItemsGrid>
        {sortedItems.map((item) => {
          const entry = findEntryFromItem(item.itemId);
          const category = itemToCategoryMap[item.itemId];

          // Calculate nrInCart: items in current cart + items already in cart (from nrInCartMap)
          const itemsInCurrentCart = items.filter((cartItem) => cartItem.itemId === item.itemId)
            ?.length;
          const alreadyInCartCount = nrInCartMap[item.itemId] || 0;
          const totalInCart = itemsInCurrentCart + alreadyInCartCount;

          return (
            <ItemButton
              key={item.itemId}
              onAddItem={handleAddItem}
              title={item.title}
              description={item.descriptionLang}
              itemId={item.itemId}
              itemToCategoryMap={itemToCategoryMap}
              venueId={venueId}
              color={category?.color || '#fff'}
              manualPrice={item.manualPrice}
              volumeSuffix={item.volumeSuffix}
              nrInCart={totalInCart}
              count={count}
              buttonWidth={100}
              isScrolling={isScrolling}
              hasServiceCharge={hasServiceCharge}
              price={entry?.price}
            />
          );
        })}
      </ItemsGrid>
    </>
  );
};

export default StagedBundleGrid;
